import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'

import useBlickPlusOnboardingStepsABTest from '@hooks/useBlickPlusOnboardingStepsABTest'

export type Step = 'welcome' | 'app' | 'newsletter' | 'highlights'

export interface OnboardingUtilities {
  onNext: () => void
  onPrevious: () => void
  onClose: () => void
}

const BlickPlusOnboardingManager: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const steps = useBlickPlusOnboardingStepsABTest()
  const [currentStep, setCurrentStep] = useState<number>(-1)

  useEffect(() => {
    if (steps?.length) {
      setCurrentStep((prevValue) => {
        if (prevValue > -1) {
          return prevValue
        }
        return 0
      })
    }
  }, [steps])

  useEffect(() => {
    queryClient.setQueryData<number>(
      ['blick-plus-onboarding-current-step'],
      currentStep,
    )
    queryClient.invalidateQueries({
      queryKey: ['blick-plus-onboarding-current-step'],
      exact: true,
    })
  }, [queryClient, currentStep])

  const onNext = useCallback(() => {
    if (!steps) {
      return
    }
    setCurrentStep((prevValue) => {
      if (prevValue < 0 || prevValue === steps.length - 1) {
        return prevValue
      }
      return prevValue + 1
    })
  }, [steps])

  const onPrevious = useCallback(() => {
    setCurrentStep((prevValue) => {
      if (prevValue < 1) {
        return prevValue
      }
      return prevValue - 1
    })
  }, [])

  const onClose = useCallback(() => {
    setCurrentStep(-1)
  }, [])

  const utilities = useMemo(() => {
    return {
      onNext,
      onPrevious,
      onClose,
    }
  }, [onClose, onNext, onPrevious])

  useEffect(() => {
    queryClient.setQueryData<OnboardingUtilities>(
      ['blick-plus-onboarding-utilities'],
      utilities,
    )
    queryClient.invalidateQueries({
      queryKey: ['blick-plus-onboarding-utilities'],
      exact: true,
    })
  }, [queryClient, utilities])

  return null
}

export default BlickPlusOnboardingManager
